import { useMediaQuery } from '@withjoy/joykit/utils';
import { useCallback, useMemo } from 'react';
import { ProductGridHighlightsProps } from './ProductGridHighlights';
import { PRODUCT_GRID_BREAKPOINTS } from '../../../../Catalog.constants';
import { useRouteMatch } from '@react-router';
import { useEventTypeConfig } from '@shared/utils/eventType';
import { useFeatureValue } from '@shared/core/featureFlags';
import { HIGHLIGHTS } from './ProductGridHighlights.constants';
import { MinimalProductWithDetails } from '../../../../utils/createTelemetryPayload';
import { useCatalogLoggingContext } from '../../../../Catalog.logging';

export const useProductGridHighlights = () => {
  const smallMediaQuery = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: PRODUCT_GRID_BREAKPOINTS[1] }));
  const mediumMediaQuery = useMediaQuery(theme => theme.mediaQueries.between({ breakpointAlias: PRODUCT_GRID_BREAKPOINTS[1] }, { breakpointAlias: PRODUCT_GRID_BREAKPOINTS[2] }));
  const largeMediaQuery = useMediaQuery(theme => theme.mediaQueries.between({ breakpointAlias: PRODUCT_GRID_BREAKPOINTS[2] }, { breakpointAlias: PRODUCT_GRID_BREAKPOINTS[3] }));
  const extraLargeMediaQuery = useMediaQuery(theme =>
    theme.mediaQueries.between({ breakpointAlias: PRODUCT_GRID_BREAKPOINTS[3] }, { breakpointAlias: PRODUCT_GRID_BREAKPOINTS[4] })
  );

  const routeMatch = useRouteMatch({ path: Object.keys(HIGHLIGHTS) }) as { path: keyof typeof HIGHLIGHTS } | null;

  const { config } = useEventTypeConfig();
  const { payload } = useFeatureValue('shopSubCatHighlightsExperiment');
  const { trackProductTileClickAction } = useCatalogLoggingContext();

  const trackTileClick = useCallback(
    ({ product, position }: { product: MinimalProductWithDetails; position: number }) => {
      // numeric position indexes aren't as helpful as the string values
      const method = <'productTileGood' | 'productTileBetter' | 'productTileBest'>['productTileGood', 'productTileBetter', 'productTileBest'][position];

      trackProductTileClickAction({
        product,
        position,
        page: 'subcategory',
        component: 'productGridHighlights',
        method
      });
    },
    [trackProductTileClickAction]
  );

  const subCatHighlightsVisible = useMemo(() => {
    return routeMatch && payload === true && config?.subCatHighlights;
  }, [routeMatch, config?.subCatHighlights, payload]);

  const subCatHighlightsIndex = useMemo<ProductGridHighlightsProps['positionIndex']>(() => {
    if (smallMediaQuery) {
      return 4;
    }
    if (mediumMediaQuery) {
      return 6;
    }
    if (largeMediaQuery) {
      return 8;
    }
    if (extraLargeMediaQuery) {
      return 10;
    }
    return 12;
  }, [smallMediaQuery, mediumMediaQuery, largeMediaQuery, extraLargeMediaQuery]);

  return {
    routeMatch,
    subCatHighlightsVisible,
    subCatHighlightsIndex,
    trackTileClick
  };
};
